import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Api from 'services/api'
import styled from 'styled-components'

import { BillingData } from 'types/BillingData'
import { isEmailValid, isZipValid, isCanadaPostalValid } from 'helpers/validations'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import { ButtonContainer, SinglePanePage, PageTitle, screenSize } from 'styles/shared'

import Environment from 'helpers/environment'
import GA4 from 'helpers/ga4'
import Brand from 'helpers/brand'
import WindowLocation from 'helpers/windowLocation'
import PaywallAccountFields from 'pages/paywall/PaywallAccountFields'
import StatesAndProvinces from 'components/StatesDropdown/StatesAndProvinces'
import Spinner from 'components/Spinner'

import useDealerContactInfo from 'hooks/useDealerContactInfo'
import usePaywallActiveAcct from 'hooks/usePaywallActiveAcct'

const BILLING_MESSAGE = 'To update your credit card information, please enter your billing address and select Next to continue to payment details.'

const PaywallAccount = () => {
  const { dealerId } = useParams()
  const { planNo } = useParams()
  const { planType } = useParams()
  const { account, isPaywallAcctLoaded } = usePaywallActiveAcct(dealerId)
  const { dealer, isDealerLoaded } = useDealerContactInfo()
  const [paywallAcctAttributes, setPaywallAcctAttributes] = useState<BillingData>()
  const navigate = useNavigate()

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: ''
  })

  const finalAmountNoteText = planType === 'diag_monthly'
    ? 'USD $250.00 will be billed to your account monthly.'
    : 'USD $2,400.00 will be billed to your account annually.'

  useEffect(() => {
    if (isPaywallAcctLoaded && account) {
      setPaywallAcctAttributes(account.billingContact)
    } else if (isDealerLoaded && dealer) {
      const countryCode = findCountry(dealer.state)

      setPaywallAcctAttributes({
        address2: '',
        firstName: '',
        lastName: '',
        address: dealer.address || '',
        city: dealer.city || '',
        state: dealer.state || '',
        zip: dealer.zip || '',
        email: dealer.email || '',
        country: countryCode || ''
      })
    }
  }, [isPaywallAcctLoaded, account, dealer, isDealerLoaded])

  const findCountry = (stateCode: string = '') => {
    let countryCode = 'US'

    for (let i = 0; i < StatesAndProvinces.CAN_PROVINCES.length; i++) {
      if (StatesAndProvinces.CAN_PROVINCES[i].value === stateCode) {
        countryCode = 'CA'
        break
      }
    }

    return countryCode
  }

  const onChangeCallback = (updatedAttribute) => {
    if (updatedAttribute.firstName) errors.firstName = ''
    if (updatedAttribute.lastName) errors.lastName = ''
    if (updatedAttribute.address) errors.address = ''
    if (updatedAttribute.city) errors.city = ''
    if (updatedAttribute.state) errors.state = ''
    if (updatedAttribute.zip) errors.zip = ''
    if (updatedAttribute.email) errors.email = ''

    setErrors({ ...errors })

    if (updatedAttribute.state) {
      updatedAttribute.country = findCountry(updatedAttribute.state)
    }

    setPaywallAcctAttributes({ ...paywallAcctAttributes, ...updatedAttribute })
  }

  const validateFields = () => {
    const newErrors = {
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      email: ''
    }

    if (!paywallAcctAttributes?.firstName) newErrors.firstName = 'First name is required'
    if (!paywallAcctAttributes?.lastName) newErrors.lastName = 'Last name is required'
    if (!paywallAcctAttributes?.address) newErrors.address = 'Address is required'
    if (!paywallAcctAttributes?.city) newErrors.city = 'City is required'
    if (!paywallAcctAttributes?.state) newErrors.state = 'State is required'
    if (!paywallAcctAttributes?.zip) newErrors.zip = 'Zip code is required'
    if (!paywallAcctAttributes?.email) newErrors.email = 'Email is required'

    const countryCode = findCountry(paywallAcctAttributes?.state) || 'US'

    if (countryCode === 'CA' && paywallAcctAttributes?.zip && !isCanadaPostalValid(paywallAcctAttributes.zip)) newErrors.zip = 'Invalid postal code'
    if (countryCode === 'US' && paywallAcctAttributes?.zip && !isZipValid(paywallAcctAttributes.zip)) newErrors.zip = 'Invalid zip code'

    if (paywallAcctAttributes?.email && !isEmailValid(paywallAcctAttributes.email)) newErrors.email = 'Invalid email'

    return newErrors
  }

  const onClickEnterCC = (testAcct: boolean) => {
    const newErrors = validateFields()

    if (newErrors.firstName || newErrors.lastName || newErrors.address || newErrors.city || newErrors.state || newErrors.zip || newErrors.email) {
      setErrors(newErrors)
    } else if (paywallAcctAttributes) {
      setErrors(newErrors)
      const text = `Click CC - planNo: ${planNo}`

      GA4.sendEvent({ category: 'Dealer Paywall', label: 'Dealer Paywall Add CC', action: `${text}` })

      if (testAcct) {
        Api.Paywall.setupTestAccount(Number(dealerId), Number(planNo), paywallAcctAttributes)
          .then(() => {
            navigate('/dashboard/paywall/accept')
          }).catch(() => {
            navigate('/dashboard/paywall/failure')
          })
      } else {
        Api.Paywall.triggerCybersource(Number(dealerId), Number(planNo), paywallAcctAttributes)
          .then(res => {
            WindowLocation.assign(res.postUrl + '/?' + (new URLSearchParams(res.cybersourceFields).toString()))
          }).catch()
      }
    }
  }

  if (!isPaywallAcctLoaded || !isDealerLoaded) {
    return <Spinner variant="page" />
  }

  const buttonParams = { button: { height: 32, borderRadius: 5, width: 240 } }

  return (
    <>
      <S.Top>
        <S.ProductLogo src={Brand.premiumProductLogoPath()} alt={`${Brand.brandName()} logo`} />
        <div />
      </S.Top>
      <SinglePanePage>
        <S.PageTitle>Billing Information</S.PageTitle>
        <S.Form>
          <S.InnerContainer>
            <S.Explanation>{BILLING_MESSAGE}</S.Explanation>
            <PaywallAccountFields {...{ ...paywallAcctAttributes, errors, onChangeCallback }} />
          </S.InnerContainer>
        </S.Form>
        <S.FinalAmountNote>{finalAmountNoteText}</S.FinalAmountNote>
        <S.ButtonContainer>
          <Button decoration="secondary" text="Back" styles={buttonParams} onPress={() => navigate(-1)} />
          <Button decoration="positive" text="Next" onPress={() => onClickEnterCC(false)} styles={buttonParams} />
          {!Environment.isProduction() && !Environment.isStaging() &&
            <Button decoration="danger" text="Test Account - NO CC"
              onPress={() => onClickEnterCC(true)} styles={buttonParams}
            />}
        </S.ButtonContainer>
      </SinglePanePage>
    </>
  )
}

const S = {
  ButtonContainer: styled(ButtonContainer)`
    border-top: none;
    max-width: 821px;
    margin-top: 0px;
    padding-top: 0px;
    @media ${screenSize.small} {
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 125px;
      margin-bottom: 80px;
    }
  `,
  Explanation: styled.div`
    display: flex;
    margin-bottom: 20px;
    text-align: left;
  `,
  FinalAmountNote: styled.span`
    display: block;
    text-align: right;
    max-width: 821px;
    margin: 15px 0 0;
    width: 100%;
    font-weight: 800;
    font-size: 16px;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
  `,
  InnerContainer: styled.div`
    display: flex;
    flex-direction: column;
    background-color: #F3F7F8;
    padding: 10px 15px 15px 15px;
    border-radius: 10px;
  `,
  Form: styled.form`
    display: flex;
    grid-column-gap: 50px;
    flex-wrap: wrap;
    justify-content: space-between;
  `,
  ProductLogo: styled.img`
    height: 50px;
    @media ${screenSize.small} {
      max-width: 290px;
    }
  `,
  Top: styled.div`
    display: flex;
    padding-top: 10px;
    padding-left: 20px;
  `
}

export default PaywallAccount
